<script>
   /**********************************************************************/
   /* PlotSeries                                                         */
   /* --------------------                                               */
   /* shows line-scatter-bar series with selection and color grouping    */
   /* possibility                                                        */
   /**********************************************************************/

   import { getContext } from 'svelte';
   import { range } from 'mdatools/stat';
   import { val2p, checkCoords, transformCoords, transformObjects, handleClick } from 'svelte-plots-basic/utils';
   import { LABELS_MARGIN, MARKER_SYMBOLS } from 'svelte-plots-basic/constants';

   import { LINE_COLORS, LABELS_COLOR, LABELS_COLOR_SELECTED, MAX_LABEL_SYMBOLS } from './Defaults.js';
   import { seq } from '../utils.js';

   let {
      xValues,                            // vector or array with x-values
      yValues,                            // vector or array with y-values
      plotType,                           // type of plot ('p', 'l', 'h', 'b')
      labels,                             // array with labels for plot elements
      showLabels,                         // show or not labels for plot elements
      shortenLabels = true,               // do we need to shorten labels
      lineWidth = 1.0,                    // width of the lines and bar outlines, px
      nBars = 1,                          // number of bars in the plot
      barNum = 1,                         // if more than one bar, which one is this
      markerSize = null,                  // marker size (if null will be set automatically)
      markerWidth = 2.0,                  // thickness of marker
      marker = 1,                         // point character (marker)
      colors = undefined,                 // color for every point or bar
      filled = undefined,                 // vector with logical values for every point (if true will be filled same color)
      mainColor = LINE_COLORS['cal'],     // main color if color grouping is not used
      faceColor = null,                   // face color for the points
      lineColor = mainColor,
      selected = -1,                      // selected data point
      maskSelected = true,                // add semi-transparent mask when value is selected or not
      dimmed = undefined,                 // dim the series, by default when it holds the selection
      xRange = $bindable(),               // [min, max] of the x-values, of the rows in 'ind' if given
      yRange = $bindable(),               // [min, max] of the y-values, of the rows in 'ind' if given
      callbacks,                          // callback for onclick events
      ind = null
   } = $props();


   // handler for select event
   function select(e) {
      if (!callbacks || !callbacks.select) return;
      handleClick(e, e.target.tagName, (x) => callbacks.select(x, ind));
   }

   // handler for unselect event
   function unselect(e) {
      if (!callbacks || !callbacks.unselect) return;
      callbacks.unselect();
   }


   // get axes context
   const axes = getContext('axes');

   // check coordinates
   const xv = $derived(checkCoords(xValues, 'PlotSeries'));
   const yv = $derived(xv ? checkCoords(yValues, 'PlotSeries', xv.length) : null);

   // compute x-limits
   $effect(() => {
      if (ind) {
         const ind1 = ind.map(v => v + 1);
         xRange = xv ? range(xv.subset(ind1)) :  null;
         yRange = yv ? range(yv.subset(ind1)) :  null;
      } else {
         xRange = xv ? range(xv) :  null;
         yRange = yv ? range(yv) :  null;
      }
   });

   // face color, marker and size of markers
   const fcol = $derived(faceColor === null ? (plotType === 'p' ? 'transparent' : 'white') : faceColor);
   const msizes1 = [1, 0.8, 0.8, 0.8, 0.8, 0.8, 0.8, 0.8];
   const msizes2 = [0.85, 0.65, 0.65, 0.65, 0.65, 0.65, 0.65, 0.65];

   const msize = $derived(!markerSize ? (plotType==='p' ? msizes1[marker - 1] : msizes2[marker - 1]) : markerSize);
   const pch = $derived(MARKER_SYMBOLS[marker - 1]);

   // reactive calculations for bar plot parameters
   const m = $derived(LABELS_MARGIN[axes.scales().plot]);
   const p0 = $derived(transformCoords([0.0], axes.tY())[0]);
   const bwf = $derived(transformObjects([0.8], axes.tX())[0]);
   const bw = $derived(plotType === 'h' ? bwf / nBars : 0);
   const bhw = $derived(plotType === 'h' ? bwf / 2.0 - (barNum - 1) * bw : 0);
   const dy = $derived(m);

   // reactive calculations for coordinates
   let lp = $derived( plotType === 'l' || plotType === 'b' ? val2p(xv, yv, axes.tX(), axes.tY()) : null);
   let px = $derived(xv ? transformCoords(xv, axes.tX()) : null);
   let py = $derived(yv ? transformCoords(yv, axes.tY()) : null);

   // truncate the labels
   let labelsShort = $derived(labels && Array.isArray(labels) ?
      (shortenLabels ? labels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : labels) :
      null
   );

   const pi = $derived(ind ? ind : seq(0, px.length - 1))

   // reactive expression for colors
   let pc = $derived(colors && colors.length === xv.length ? colors : Array(xv.length).fill(mainColor));
   let pff = $derived(filled && filled.length === xv.length ? filled.map(v => v ? 'ff' : '20') : Array(xv.length).fill('20'));
   let pfc = $derived(colors && colors.length === xv.length ? colors.map((v, i) => v + pff[i]) : Array(xv.length).fill(faceColor));

   // a plot whose series do not all hold the selection passes 'dimmed' to each, so all of them dim
   const dim = $derived(dimmed ?? selected >= 0);

   // styles for points, bars, labels, etc.
   const ptStyle = $derived(`font-size:${msize}em;fill:${fcol};cursor:default;text-anchor:middle;stroke-width:${markerWidth}px;user-select:none;`);
   const lbStyle = `font-size:0.85em;fill:${LABELS_COLOR};cursor:default;text-anchor:middle;stroke:${LABELS_COLOR};stroke-width:0.1px;user-select:none;`;
   const slStyle = $derived(`font-size:${msize}em;fill:none;text-anchor:middle;text-anchor:middle;cursor:default;stroke-width:2px;user-select:none;`);
   const lineStyleStr = $derived(`fill:transparent;stroke-width:${lineWidth}px;`);
   const barsStyleStr = $derived(`stroke-width:${lineWidth}px;stroke:${lineColor};`);

   // remove selection if the selected value is not among provided indices. the test is '>= 0' and
   // not truthiness: nothing selected is -1, so object index 0 is a valid selection and must be
   // checked like any other
   $effect(() => {
      if (selected >= 0 && ind && ind.indexOf(selected) < 0) callbacks.unselect();
   })
</script>

{#if px && py}

   {#if showLabels && labelsShort}
      <!-- labels -->
      <g class="series series-textlabels" style={lbStyle + (dim ? "opacity:0.2;":"")}>
      {#each pi as i}
      <text x={px[i] - bhw  + bw/2} y={py[i]} dy={yv.v[i] < 0 ? dy : -dy}>{labelsShort[i]}</text>
      {/each}
      </g>
   {/if}


   {#if plotType === 'l' || plotType === 'b'}
      <!-- line plot -->
      <g class="series series-line" style={lineStyleStr}>
         <polyline class="line" points={lp} stroke={mainColor}/>
      </g>
   {/if}

   {#if plotType === 'h'}
      <!-- bar plot -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <g class="series series-bar" style={barsStyleStr + (dim ? "opacity:0.2;":"")} onclick={select}>
      {#each pi as i}
      <!-- the border is set on every bar and not on the group above, so that it follows the color
           grouping the way the fill does. without it the bars inherit the single 'stroke' of the
           group and keep one border color whatever they are colored by -->
      <rect x={px[i] - bhw} y={(yv.v[i] < 0) ? p0 : py[i]} fill={pc[i]} stroke={pc[i]} width={bw} height={yv.v[i] < 0 ? py[i] - p0 : p0 - py[i]} />
      {/each}
      </g>
   {/if}

   {#if plotType === 'p' || plotType === 'b'}
      <!-- scatter/points plot -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <g class="series series-points series-points1" style={ptStyle + (dim ? "opacity:0.2;":"")} onclick={select}>
      {#each pi as i}
      <text x={px[i]} y={py[i]} fill={pfc[i]} stroke={pc[i]}>{pch}</text>
      {/each}
      </g>
   {/if}


   {#if selected >= 0}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      {#if maskSelected}
      <rect x={0} y={0} width='100%' height='100%' fill="#ffffff00" onclick={unselect}/>
      {/if}
      {#if plotType !== 'h'}
      <g class="series series-points series-points1" style={slStyle}>
      <text x={px[selected]} y={py[selected]} stroke={pc[selected]} fill={pfc[selected]}>{pch}</text>
      </g>
      {:else}
      <g class="series series-bar" style={barsStyleStr}>
      <rect stroke={pc[selected]} fill={pc[selected]} x={px[selected] - bhw} y={(yv.v[selected] < 0) ? p0 : py[selected]} width={bw} height={yv.v[selected] < 0 ? py[selected] - p0 : p0 - py[selected]} />
      </g>
      {/if}

      {#if showLabels && labelsShort && labelsShort.length > 0}
      <g class="series series-text" style={lbStyle}>
      <text stroke={LABELS_COLOR_SELECTED} fill={LABELS_COLOR_SELECTED} x={px[selected]} y={py[selected]} dy={yv.v[selected] < 0 ? dy : -dy}>{labelsShort[selected]}</text>
      </g>
      {/if}
   {/if}
{/if}